import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);
const TOKEN_KEY = "relate_jwt";

function getToken() { return localStorage.getItem(TOKEN_KEY); }
function setToken(t) { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); }

axios.interceptors.request.use((config) => {
  const t = getToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/usage`);
      setUsage(data);
    } catch {
      setUsage(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.user);
      await loadUsage();
    } catch {
      setUser(null);
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, [loadUsage]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setToken(data.token); setUser(data.user);
    await loadUsage();
    return data.user;
  };

  const register = async (email, password, name, inviteToken) => {
    const payload = { email, password, name };
    if (inviteToken) payload.invite_token = inviteToken;
    const { data } = await axios.post(`${API}/auth/register`, payload);
    setToken(data.token); setUser(data.user);
    await loadUsage();
    return data.user;
  };

  const loginGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSessionId = async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/session`, null, {
      headers: { "X-Session-ID": sessionId },
    });
    setUser(data.user);
    await loadUsage();
    return data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    setToken(null); setUser(null); setUsage(null);
  };

  const setAuthFromToken = useCallback(async (jwt, userObj) => {
    setToken(jwt);
    setUser(userObj);
    await loadUsage();
  }, [loadUsage]);

  return (
    <AuthContext.Provider value={{
      user, usage, loading,
      login, register, loginGoogle, logout, processSessionId,
      refresh: checkAuth, refreshUsage: loadUsage, setAuthFromToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
